import { useId, type InputHTMLAttributes } from "react";

import { cn } from "@/lib/cn";

interface FieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "id"> {
  label: string;
  error?: string;
  /** Marks the field as incomplete without being a hard validation error. */
  missing?: boolean;
}

export function Field({ label, error, missing, className, ...rest }: FieldProps) {
  // useId keeps label/input association correct even when the same field
  // component is rendered more than once on a page.
  const id = useId();
  const errorId = `${id}-error`;

  return (
    <div className={className}>
      <label className="field-label" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : undefined}
        className={cn(
          "field-input",
          missing && !error && "border-warn",
          error && "border-danger",
        )}
        {...rest}
      />
      {error && (
        <p id={errorId} className="mt-1.5 text-[12.5px] text-danger">
          {error}
        </p>
      )}
    </div>
  );
}

interface SelectFieldProps {
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
  className?: string;
  disabled?: boolean;
}

export function SelectField({
  label,
  value,
  options,
  onChange,
  className,
  disabled,
}: SelectFieldProps) {
  const id = useId();
  return (
    <div className={className}>
      <label className="field-label" htmlFor={id}>
        {label}
      </label>
      <select
        id={id}
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        className={cn("field-input", disabled && "cursor-not-allowed opacity-60")}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}
